// §4.2 Overview — THE DEALS AND LATEST ORDERS PANELS (Module 09, P3-E2 PR
// E2-1, D-313 ruling 9). The Tasks panel is the open-task card
// (components/sales-tasks.tsx) with a customer subject.
//
// EACH IS A LAZY READ, as the quote panel beside them is: §4.2's < 2 s budget
// is met by the ONE `GET /v1/customers/{id}`, so nothing a panel needs rides
// that request, and a failed read drops the panel to a single line rather
// than turning the customer record into an error card.
//
// THE DEALS PANEL lists the customer's OPEN deals (an absent `status` is
// open) in the deal list's declared order, by close month. Each row shows the
// deal's number and title, its stage in the reader's language beside its
// probability, the month it is expected to close (red once it has passed),
// the value for a value reader and the next step or the flag. It is offered to
// the readers of the pipeline (§7 narrowing 15): a reader the role mirror
// knows is refused sends no request and sees no panel. New deal opens the
// board's New deal dialog with this customer locked
// (`/sales/pipeline?new_deal=<id>`, §4.14), for a holder of
// `sales.opportunity.write` on an active customer.
//
// THE PANEL GRID REVEALS ONCE (design-system §5). The panels' reads land at
// different times and each changed its panel's height, which moved the whole
// second row of the grid after the page appeared (the local walk measured a
// layout shift of 0.035 to 0.071 on every reload). `useOverviewSettled` asks
// every read the Overview's panels paint, with the panels' own keys, and the
// panel grid waits behind one reserving card per panel
// (`overviewPanelSlots`) until all of them have answered, failed or paused
// offline. The open AR and primary-contact cards ahead of the grid come from
// the one GET and never wait (spec 09 §4.2, E2-1 wave 2). A failed read still
// opens the grid: that panel then says so in its one line, and a read held
// offline says the network is gone in its one line, never a skeleton. The
// reveal latches for the customer (`useRevealLatch`, lib/reads.ts): a read
// asked again after it, a failed one refetched when the window regains focus
// or a paused one when the network returns, renders in its own panel and
// never takes the grid back to its reserving cards, or an open Add task
// dialog with it.
//
// THE MEMBER DIRECTORY OFFLINE. An admin's directory read is paused, not
// failed, when the browser has no network, and `useOwnerIndex` reports a
// paused read as pending, which alone would hold the grid for as long as the
// cut lasts. While react-query holds its reads offline, the directory counts
// as settled, and the owner names read as short ids until it answers (the
// contact card's owner line included, customer-360.tsx).
//
// THE LATEST ORDERS PANEL lists the five newest orders (`-created_at`, so an
// order still waiting for approval, which has no confirmation date yet, is not
// pushed to the end) with their status and, for a value reader, their total,
// and links to the order book filtered to the customer. §7's money is
// OMISSION: a row without `total_minor` shows no figure at all.

import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { StatusChip } from "../../../../components/status-chip";
import { formatTimestamp } from "../../../../lib/datetime";
import { useTenantTimezone } from "../../../../lib/role-codes";
import { formatMinor } from "../lib/money";
import { formatMonth, stageName } from "../lib/pipeline";
import { readSettled, useReadsHeldOffline } from "../lib/reads";
import { useOrders, useQuotes } from "../hooks/quote-queries";
import { useOpportunities, usePipelineStages } from "../hooks/opportunity-queries";
import { useDocumentTasks, useRecentDoneTasks } from "../hooks/activity-queries";
import { useOwnerIndex } from "../hooks/sales-queries";
import {
  canWriteOpportunities,
  isSalesReadRefused,
  useSalesPermissions,
  type SalesPermissions,
} from "../hooks/permissions";
import { NextStepCell, StageText } from "./pipeline/pipeline-parts";
import { orderStatusVariant } from "../screens/orders";

/** How many rows each panel shows. A glance inside a customer record; the
 * link beneath each panel is the way to all of them. */
export const OVERVIEW_PANEL_LIMIT = 5;

/** The panels' own queries, so the reveal gate and the panels share keys. The
 * quote panel (components/customer-quotes.tsx) asks the same five. */
export function overviewQueries(customerId: string) {
  return {
    deals: { customer_id: customerId, limit: OVERVIEW_PANEL_LIMIT },
    quotes: { customer_id: customerId, limit: OVERVIEW_PANEL_LIMIT },
    orders: { customer_id: customerId, limit: OVERVIEW_PANEL_LIMIT, order: "-created_at" as const },
  };
}

/** How many panels the grid will show this reader, so the reveal reserves one
 * card per panel in the cell it takes: the tasks for a task writer, the deals
 * for a reader of the pipeline, and the quotes, the orders and the
 * assignments for everyone. */
export function overviewPanelSlots(
  perms: Pick<SalesPermissions, "isResolved" | "isFeedError" | "has" | "canOpenPipeline">,
): number {
  let slots = 3;
  if (perms.has("sales.activity.write")) slots += 1;
  if (!isSalesReadRefused(perms, perms.canOpenPipeline)) slots += 1;
  return slots;
}

/**
 * Has every read the panel grid paints finished (the header)? The deals and
 * the stage names for a reader of the pipeline, the latest quotes and orders,
 * the open tasks (all, and mine for a reader who is not an admin) and the
 * latest done for a task writer, and the member directory for an admin, whose
 * assignment rows name the people from it.
 */
export function useOverviewSettled(customerId: string): boolean {
  const perms = useSalesPermissions();
  const offline = useReadsHeldOffline();
  const queries = overviewQueries(customerId);
  const askDeals = !isSalesReadRefused(perms, perms.canOpenPipeline);
  const askTasks = perms.has("sales.activity.write");
  const subject = { kind: "customer", id: customerId, customerId } as const;
  const deals = useOpportunities(queries.deals, undefined, askDeals);
  const stages = usePipelineStages(askDeals);
  const quotes = useQuotes(queries.quotes, undefined);
  const orders = useOrders(queries.orders, undefined);
  const tasks = useDocumentTasks(subject, "all", askTasks);
  const own = useDocumentTasks(subject, "mine", askTasks && !perms.isAdmin);
  const done = useRecentDoneTasks(customerId, askTasks);
  const owners = useOwnerIndex(perms.isAdmin);
  return (
    readSettled(deals, askDeals) &&
    readSettled(stages, askDeals) &&
    readSettled(quotes) &&
    readSettled(orders) &&
    readSettled(tasks, askTasks) &&
    readSettled(own, askTasks && !perms.isAdmin) &&
    readSettled(done, askTasks) &&
    (!perms.isAdmin || !owners.isPending || offline)
  );
}

export function CustomerDealsPanel({
  customerId,
  customerActive,
}: {
  readonly customerId: string;
  /** New deal is offered on an active customer only: an archived customer
   * holds no open deal (the create answers 422 `CUSTOMER_ARCHIVED`). */
  readonly customerActive: boolean;
}) {
  const perms = useSalesPermissions();
  // Narrowing 15's lineages. A composed role the mirror cannot judge is let
  // through, and the server's refusal reads as the panel's one line.
  if (isSalesReadRefused(perms, perms.canOpenPipeline)) return null;
  return <CustomerDealsBody customerId={customerId} customerActive={customerActive} />;
}

function CustomerDealsBody({
  customerId,
  customerActive,
}: {
  readonly customerId: string;
  readonly customerActive: boolean;
}) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const deals = useOpportunities(overviewQueries(customerId).deals, undefined);
  const stages = usePipelineStages(true);
  const stageById = new Map((stages.data?.items ?? []).map((stage) => [stage.id, stage]));
  const rows = deals.data?.items ?? [];
  const offerNew = customerActive && canWriteOpportunities(perms);

  return (
    <div className="ds-card sales-360-card" data-testid="sales-360-deals">
      <h3>{t("sales.c360.overview.dealsTitle")}</h3>
      {deals.error !== null ? (
        <p className="sales-hint" data-testid="sales-360-deals-error">
          {t("sales.c360.overview.dealsError")}
        </p>
      ) : null}
      {deals.data === undefined && deals.error === null && deals.fetchStatus === "paused" ? (
        <p className="sales-hint" role="status" data-testid="sales-360-deals-offline">
          {t("sales.offline.body")}
        </p>
      ) : null}
      {deals.error === null && !deals.isPending && rows.length === 0 ? (
        <p className="sales-hint" data-testid="sales-360-deals-empty">
          {t("sales.c360.overview.dealsEmpty")}
        </p>
      ) : null}
      <ul className="sales-360-quote-list">
        {rows.map((deal) => {
          const stage = stageById.get(deal.stage_id);
          return (
            <li key={deal.id} data-testid="sales-360-deal-row" data-deal-id={deal.id}>
              <Link
                to="/sales/opportunities/$opportunityId"
                params={{ opportunityId: deal.id }}
                className="ds-lnk"
                data-testid="sales-360-deal-link"
              >
                {t("sales.composer.dealChip", { no: deal.opportunity_no, title: deal.title })}
              </Link>
              {stage !== undefined ? (
                <StageText name={stageName(stage, i18n.language)} pct={deal.probability_pct} />
              ) : null}
              <span
                className={deal.close_month_passed ? "num sales-valid-urgent" : "num"}
                data-testid="sales-360-deal-close"
                data-passed={deal.close_month_passed ? "true" : "false"}
              >
                {formatMonth(deal.expected_close_month, i18n.language)}
              </span>
              {/* §7 OMISSION, and NULL is a different fact: nobody estimated
                  the value, which no reader counts as zero. */}
              {deal.expected_value_minor !== undefined && deal.expected_value_minor !== null ? (
                <span className="num" data-testid="sales-360-deal-value">
                  {formatMinor(deal.expected_value_minor, deal.currency, i18n.language)}
                </span>
              ) : null}
              <NextStepCell next={deal.next_activity} needsNextStep={deal.needs_next_step} />
            </li>
          );
        })}
      </ul>
      <p className="sales-toolbar">
        <Link
          to="/sales/opportunities"
          search={{ customer: customerId }}
          className="ds-lnk"
          data-testid="sales-360-deals-more"
        >
          {t("sales.c360.overview.dealsMore")}
        </Link>
        {offerNew ? (
          <Link
            to="/sales/pipeline"
            search={{ new_deal: customerId }}
            className="ds-lnk"
            data-testid="sales-360-deal-new"
          >
            {t("sales.pipeline.newDeal")}
          </Link>
        ) : null}
      </p>
    </div>
  );
}

export function CustomerOrdersPanel({ customerId }: { readonly customerId: string }) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const timeZone = useTenantTimezone();
  const orders = useOrders(overviewQueries(customerId).orders, undefined);
  const rows = orders.data?.items ?? [];
  return (
    <div className="ds-card sales-360-card" data-testid="sales-360-orders">
      <h3>{t("sales.c360.overview.ordersTitle")}</h3>
      {orders.error !== null ? (
        <p className="sales-hint" data-testid="sales-360-orders-error">
          {t("sales.c360.overview.ordersError")}
        </p>
      ) : null}
      {orders.data === undefined && orders.error === null && orders.fetchStatus === "paused" ? (
        <p className="sales-hint" role="status" data-testid="sales-360-orders-offline">
          {t("sales.offline.body")}
        </p>
      ) : null}
      {orders.error === null && !orders.isPending && rows.length === 0 ? (
        <p className="sales-hint" data-testid="sales-360-orders-empty">
          {t("sales.c360.overview.ordersEmpty")}
        </p>
      ) : null}
      <ul className="sales-360-quote-list">
        {rows.map((order) => (
          <li key={order.id} data-testid="sales-360-order-row" data-status={order.status}>
            {/* A link only into a screen the reader opens (D-088). */}
            {perms.canOpenOrders ? (
              <Link
                to="/sales/orders/$orderId"
                params={{ orderId: order.id }}
                className="ds-lnk"
                data-testid="sales-360-order-link"
              >
                {order.order_no}
              </Link>
            ) : (
              <span data-testid="sales-360-order-no">{order.order_no}</span>
            )}
            <StatusChip variant={orderStatusVariant(order.status)} dot={false}>
              {t(`sales.orders.status.${order.status}`)}
            </StatusChip>
            {order.total_minor !== undefined ? (
              <span className="num" data-testid="sales-360-order-total">
                {formatMinor(order.total_minor, order.currency, i18n.language)}
              </span>
            ) : null}
            {/* When the order was confirmed, else placed, in the tenant's
                clock (D-089). */}
            <span className="num">
              {formatTimestamp(order.confirmed_at ?? order.created_at, i18n.language, timeZone)}
            </span>
          </li>
        ))}
      </ul>
      {perms.canOpenOrders ? (
        <p>
          <Link
            to="/sales/orders"
            search={{ customer: customerId }}
            className="ds-lnk"
            data-testid="sales-360-orders-more"
          >
            {t("sales.c360.overview.ordersMore")}
          </Link>
        </p>
      ) : null}
    </div>
  );
}
